import { useCallback } from 'react'
import useSearchParamsEx from '@hook/useSearchParamsEx'
import { useQuery, useMutation } from '@tanstack/react-query'
import * as actions from '$actions'
import useCheckableItems from '@hook/useCheckableItems'
import { toast } from 'react-toastify'

const changeOrderDirection = prev =>
    prev === 'asc' ? 'desc' : 'asc'

const BenchmarksTableViewModel = () => {
    
    const [searchParams, updateSearchParams] = useSearchParamsEx({ q: '' })
    
    const {
        /** @type GBenchmark[] */
        data: benchmarks,
        error: benchmarksError,
        isLoading: benchmarksIsLoading,
        refetch: refetchBenchmarks,
    } = useQuery({
        queryKey: [
            'benchmarks',
            searchParams.get('topic'),
            searchParams.get('offset'),
            searchParams.get('limit'),
            searchParams.get('orderBy'),
            searchParams.get('orderDirection'),
        ],
        queryFn: () => actions.fetchBenchmarks(
            searchParams.get('topic'),
            searchParams.get('offset'),
            searchParams.get('limit'),
            searchParams.get('orderBy'),
            searchParams.get('orderDirection'),
        ).catch(e => {
            console.error('@todo show fetchBenchmarks error', e)
            toast.error('Failed to fetch benchmarks')
            return null
        }),
        retry: 0,
        keepPreviousData: true,
    })
    
    const { mutate: deleteBenchmarksByIds } = useMutation({
        queryKey: ['deleteBenchmarksByIds', refetchBenchmarks],
        mutationFn: ids => actions.deleteBenchmarks(ids),
        onSuccess: () => {
            refetchBenchmarks()
            toast.success('Benchmarks deleted')
        },
        onError: e => {
            console.error('Error deleting benchmarks', e)
            toast.error('Failed to delete benchmarks')
        },
    })
    
    const {
        checked,
        anyChecked,
        allChecked,
        someChecked,
        hasChecked,
        setChecked,
        toggleChecked,
        toggleAllChecked,
    } = useCheckableItems(benchmarks || [])
    
    const updateSort = useCallback(key => updateSearchParams({
        orderBy: key,
        orderDirection: searchParams.get('orderBy') === key
            ? changeOrderDirection(searchParams.get('orderDirection'))
            : searchParams.get('orderDirection') ?? 'desc',
    }), [searchParams.get('orderBy'), searchParams.get('orderDirection')])
    
    const onDeleteClick = useCallback(async id => {
        
        await deleteBenchmarksByIds([id])
        
        setChecked(id, false)
        
    }, [checked])
    
    const onDeleteCheckedClick = useCallback(async () => {
        
        await deleteBenchmarksByIds(checked)
        
        setChecked(checked, false)
        
    }, [checked])
    
    return {
        
        // Hooks
        searchParams,
        updateSearchParams,
        checked,
        anyChecked,
        allChecked,
        someChecked,
        hasChecked,
        setChecked,
        toggleChecked,
        toggleAllChecked,
        
        // React Query
        benchmarks,
        benchmarksError,
        benchmarksIsLoading,
        refetchBenchmarks,
        
        // React Query Mutations
        deleteBenchmarksByIds,
        
        // Methods
        changeOrderDirection,
        updateSort,
        onDeleteClick,
        onDeleteCheckedClick,
        
    }
    
}

export default BenchmarksTableViewModel
